<script setup>
import timVideosJson from "~/utils/tim-videos/posts.json";

const content = ref();
const sidebar = ref();
const $config = useRuntimeConfig();
const route = useRoute();

const page_slug = route.params.slug?.[0] || route.params.slug;

// Find the video synchronously
const tim_video = timVideosJson.find((item) => item.slug === page_slug);

// Throw 404 immediately if video doesn't exist
if (!tim_video) {
	throw createError({
		statusCode: 404,
		statusMessage: "Page Not Found",
		fatal: true
	});
}

// Build the post data synchronously since we already have all the data
const post = {
	id: tim_video.id,
	title: tim_video.title.rendered,
	date: tim_video.modified_gmt ? tim_video.modified_gmt : tim_video.dateGmt,
	video: tim_video.acf.youtube_url
};

const metadata = tim_video.yoast_head_json;

// Parse schema_json synchronously
let schema_json = JSON.parse(tim_video.yoast_head_json.schema_json);
schema_json = parse_saswpSchema(
	schema_json["@graph"],
	$config.public.hostname,
	$config.public.current_hostname
);

// Register lifecycle hooks (no await needed since data is synchronous)
onMounted(() => {
	if (process.client) {
		// TODO: Research why on Page Reload page scrolls to footer
		window.scrollTo(0, 0);

		if (post) {
			store.changePostId(post.id);

			setTimeout(() => {
				// eslint-disable-next-line no-new
				new YT.Player("youtube-player", {
					videoId: post.video.split("?v=")[1], // Replace with your video's ID
					playerVars: {
						autoplay: false, // Autoplay is turned off
						controls: 1, // Show video controls
						modestbranding: 1, // Hide the YouTube logo
						rel: 0 // Don't show related videos
					}
				});
			}, 250);
		}
	}
});

const head_seo = computed(() => {
	if (metadata) {
		const experiment = Object.entries(metadata);
		const filtered = experiment.filter(
			(item) =>
				![
					"title",
					"robots",
					"canonical",
					"schema_json",
					"WordPressAuthor_YoastHeadJson_Schema_"
				].includes(item[0])
		);
		if (filtered[5] && typeof filtered[5][1] === "string") {
			filtered[5][1] = filtered[5][1].replace(
				"https://content.timothysykes.com",
				"https://www.timothysykes.com"
			);
		}
		return filtered;
	}

	return null;
});

useHead({
	script: [
		{
			type: "application/ld+json",
			class: "saswp-schema-markup-output",
			innerHTML: JSON.stringify(schema_json)
		},
		{
			src: "https://www.youtube.com/iframe_api",
			async: true
		}
	]
});
</script>

<template>
	<Head>
		<Title>{{ metadata?.title }}</Title>

		<Link
			rel="canonical"
			:href="`${$config.public.current_hostname.slice(0, -1)}${route.path}`"
		/>
	</Head>

	<Head v-if="head_seo">
		<template
			v-for="(tag, i) in head_seo"
			:key="i"
		>
			<Meta
				v-if="tag[0].startsWith('og_')"
				:property="'og:' + tag[0].slice(3)"
				:content="tag[1][0].url || tag[1]"
			/>

			<Meta
				v-else-if="tag[0].startsWith('article')"
				:property="'article:' + tag[0].slice(8)"
				:content="tag[1]"
			/>
			<Meta
				v-else-if="tag[0] !== 'twitter_misc' && tag[0].startsWith('twitter')"
				:name="'twitter:' + tag[0].slice(8)"
				:content="tag[1]"
			/>

			<template v-else-if="tag[0] === 'twitter_misc'">
				<Meta
					:name="'twitter:label1'"
					:content="Object.keys(tag[1])[0]"
				/>
				<Meta
					:name="'twitter:data2'"
					:content="Object.values(tag[1])[0]"
				/>
				<Meta
					:name="'twitter:label2'"
					:content="Object.keys(tag[1])[1]"
				/>
				<Meta
					:name="'twitter:data1'"
					:content="Object.values(tag[1])[1]"
				/>
			</template>

			<Meta
				v-else-if="tag[0] != 'og_image'"
				:name="tag[0]"
				:content="tag[1]"
			/>
			<template v-else>
				<Meta
					:property="tag[0].split('_').join(':') + ':type'"
					:content="tag[1][0].type"
				/>
				<Meta
					:property="tag[0].split('_').join(':')"
					:content="tag[1][0].url"
				/>
				<Meta
					:property="tag[0].split('_').join(':') + ':width'"
					:content="tag[1][0].width"
				/>
				<Meta
					:property="tag[0].split('_').join(':') + ':height'"
					:content="tag[1][0].height"
				/>
			</template>
		</template>
	</Head>

	<BlogContainer>
		<template
			v-if="post"
			#main
		>
			<Heading
				:text="post.title"
				tag="h1"
			/>

			<!-- <section v-if="mainImageFormated" class="featured-image mb-[24px]">
        <img
          :src="mainImageFormated"
          :alt="`${post.featuredImage.node.uri} featured image`"
          class="w-full"
          width="740"
          height="532"
        />
      </section> -->

			<SharePost :type="'primary'" />

			<section
				ref="content"
				class="content"
			>
				<div class="video-container">
					<div id="youtube-player" />
				</div>
			</section>

			<Divider />

			<RatingsComponent
				id="ratings-component"
				:post-id="post.id"
			/>
		</template>
		<template
			v-if="post"
			#aside
		>
			<!-- Wrap in ClientOnly to prevent hydration mismatch from async component -->
			<ClientOnly>
				<TimothyBioPreview class="mb-[32px]" />
				<template #fallback>
					<div class="author-card-sidebar mb-[32px]">
						<img
							src="/images/author-card.webp"
							width="130"
							height="130"
							alt="Author card Timothy Sykes picture"
							class="mb-3 rounded-full object-cover h-32 w-32"
							loading="lazy"
						/>
						<h4>Timothy Sykes</h4>
						<p class="body">
							Tim Sykes is a penny stock trader and teacher who became a self-made
							millionaire by the age of 22 by trading $12,415 of bar mitzvah money.
						</p>
					</div>
				</template>
			</ClientOnly>
			<!-- <WatchlistBox class="mb-[32px]" /> -->
		</template>

		<ContentSidebar v-if="sidebar" />
	</BlogContainer>
</template>

<style lang="scss" scoped>
@import "@assets/utils.scss";
@import "@assets/blogPost.scss";

.video-container {
	margin-top: 32px;
	margin-bottom: 32px;
	height: 420px;

	@include breakpoint(md) {
		margin-top: 64px;
		margin-bottom: 64px;
		height: 620px;
	}
	@include breakpoint(xl) {
		height: 420px;
	}
}
</style>
